import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ConfirmModal from "../../common/ConfirmModal";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  fetchVolunteerProfile,
  fetchVolunteerNotes,
  fetchVolunteerActivityLog,
  fetchVolunteerStudySummary,
  saveVolunteerDocument,
  repairVolunteerDocuments,
  isBrokenVolunteerDocumentUrl,
  updateVolunteer,
  changeVolunteerStatus,
  buildVolunteerUpdatePayload,
  fetchEthnicityList,
  mapEthnicityOptions,
  getVolunteerDisplayName,
  getVolunteerRowId,
  coerceVolunteerPrefFlag,
} from "./participantService";
import {
  validateNationalMobile,
  sanitizeNationalMobileInput,
  normalizeNationalMobileInput,
  dialCodeForCountry,
  orgMobileDialLabel,
} from "../../../utils/siteFieldValidationUtils";
import {
  fetchOrganizationSetup,
  parseOrganizationSetupResponse,
} from "../../../services/organizationSetupService";
import {
  ID_NUMBER_KINDS,
  IdNumberLabel,
  useIdVerifyPrompt,
  getNhsDisplayWithVerify,
  getNiDisplayWithVerify,
  formatNhsForDisplay,
  formatNiForDisplay,
} from "./participantIdNumbers";
import { sanitizeNhsNumberInput } from "../../../utils/siteFieldValidationUtils";
import StudyHistoryPanel from "./StudyHistoryPanel";
import DocumentsPanel from "./DocumentsPanel";
import NotesPanel from "./NotesPanel";
import ClinicalHistoryPanel from "./ClinicalHistoryPanel";
import { useParticipantAccess } from "./participantAccess";
import {
  formatParticipantDate,
  getParticipantAgeYears,
  getParticipantDbLabel,
  isParticipantOnDb,
  getParticipantProfilePictureUrl,
  getVolunteerStatusLabel,
  getVolunteerStatusPillStyle,
  isVolunteerArchived,
  isVolunteerProfileReadOnly,
  PM_VOL_STATUS_OPTIONS,
  volStatusToCode,
} from "./participantReportUtils";
import ParticipantCodeLabelModal from "./ParticipantCodeLabelModal";
import AppointmentBookModal, {
  BOOK_MODES,
} from "../eplanner/appointments/AppointmentBookModal";
import AddParticipantModal from "./AddParticipantModal";
import SimpleCircularUpload from "../../common/SimpleCircularUpload";
import GenderSelector from "../../common/GenderSelector";
import Dropdown from "../../common/dropDown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import utils from "../../../utils/utils";
import "./participantmanager.scss";

const PROFILE_TABS = [
  { slug: "", label: "Details" },
  { slug: "clinical", label: "Clinical History", requiresParticipantDb: true },
  { slug: "appointments", label: "Appointments" },
  { slug: "documents", label: "Documents" },
  { slug: "notes", label: "Notes" },
  // Temporarily hidden — restore when Communications UI is ready
  // { slug: "communications", label: "Communications" },
  { slug: "audit", label: "Audit Log" },
];

function toGenderLabel(value) {
  const key = String(value ?? "")
    .trim()
    .toLowerCase();
  if (key === "m" || key === "male") return "Male";
  if (key === "f" || key === "female") return "Female";
  if (key === "o" || key === "other") return "Other";
  return value ? String(value) : "";
}
function displayOrDash(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

function getInitials(volunteer, displayName) {
  const first = String(volunteer?.FirstName ?? volunteer?.ForeName ?? "").trim();
  const last = String(volunteer?.LastName ?? volunteer?.SurName ?? "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "P";
  }
  const parts = String(displayName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "P";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function isUsablePhotoUrl(url) {
  const value = String(url || "").trim();
  if (!value) return false;
  if (value.toLowerCase().includes("avatar.png")) return false;
  return true;
}

function placeMenuNearAnchor(anchorEl, { width = 180, height = 200 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect) return { top: 8, left: 8 };
  const gap = 8;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = rect.left;
  let top = rect.bottom + gap;
  if (top + height > vh - pad) {
    const above = rect.top - height - gap;
    top = above >= pad ? above : Math.max(pad, vh - pad - height);
  }
  if (left + width > vw - pad) left = vw - pad - width;
  if (left < pad) left = pad;
  return { top, left };
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

/** Fixed viewport coords next to an anchor — same as appointment details edit menus. */
function placePopupNearAnchor(anchorEl, { width = 280, height = 220 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect || (!rect.width && !rect.height)) return { top: 8, left: 8 };
  const gap = 8;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuW = Math.max(160, Number(width) || 280);
  const menuH = Math.max(120, Number(height) || 220);

  let left = rect.left;
  let top = rect.bottom + gap;

  if (top + menuH > vh - pad) {
    const above = rect.top - menuH - gap;
    const belowClamped = vh - pad - menuH;
    if (above >= pad && above + menuH >= rect.top - 12) {
      top = above;
    } else {
      top = Math.max(pad, belowClamped);
    }
  }

  if (left + menuW > vw - pad) left = vw - pad - menuW;
  if (left < pad) left = pad;
  top = Math.min(Math.max(pad, top), Math.max(pad, vh - menuH - pad));
  left = Math.min(Math.max(pad, left), Math.max(pad, vw - menuW - pad));
  return { top, left };
}

function normalizeSelectOptions(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => {
      if (opt == null) return null;
      if (typeof opt === "string" || typeof opt === "number") {
        const value = String(opt);
        return { value, label: value };
      }
      const value = String(opt.value ?? opt.id ?? "");
      const label = String(opt.label ?? opt.name ?? value);
      if (!value) return null;
      return {
        value,
        label,
        disabled: Boolean(opt.disabled),
      };
    })
    .filter(Boolean);
}

function formatDobForSave(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : utils.parseDateInput(date);
  if (!d || Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function EditableField({
  label,
  labelText = "",
  value,
  displayValue,
  editable = true,
  inputType = "text",
  options = null,
  editor = "text",
  onSave,
  secondaryWhenValue = null,
  secondaryLabel = "",
  secondaryValue = "",
  secondaryPlaceholder = "",
  formatDraft = null,
  inputMaxLength = undefined,
  inputMode = undefined,
  dialPrefix = "",
  dialLabel = "",
  countryIso2 = "GB",
}) {
  const accessibleLabel =
    labelText ||
    (typeof label === "string" || typeof label === "number"
      ? String(label)
      : "field");
  const [editing, setEditing] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [draft, setDraft] = useState(String(value ?? ""));
  const [draftSecondary, setDraftSecondary] = useState(
    String(secondaryValue ?? ""),
  );
  const [draftDate, setDraftDate] = useState(null);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const selectOptions = useMemo(
    () => normalizeSelectOptions(options),
    [options],
  );
  const showSecondary =
    secondaryWhenValue != null &&
    String(draft).toLowerCase() === String(secondaryWhenValue).toLowerCase();

  const resolvedEditor =
    editor === "text" && selectOptions.length ? "select" : editor;

  const menuWidth =
    resolvedEditor === "textarea" ||
    resolvedEditor === "select" ||
    resolvedEditor === "phone"
      ? 300
      : 260;
  const menuHeight =
    resolvedEditor === "textarea"
      ? 220
      : resolvedEditor === "select"
        ? showSecondary
          ? 360
          : 280
        : resolvedEditor === "date"
          ? 240
          : resolvedEditor === "gender"
            ? 180
            : resolvedEditor === "phone"
              ? 220
              : 200;

  useEffect(() => {
    if (editing) return;
    setDraft(String(value ?? ""));
    setDraftSecondary(String(secondaryValue ?? ""));
    setDraftDate(utils.parseDateInput(value));
  }, [value, secondaryValue, editing]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setEditing(false);
    };
    const onPointer = (e) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      if (
        target.closest?.(
          ".react-datepicker, .react-datepicker__portal, .react-datepicker-popper",
        )
      ) {
        return;
      }
      if (
        target.closest?.(
          ".custom-dropdown, .dropdown-menu, .pm-id-guide-modal, .pm-id-label__info, .modal",
        )
      ) {
        return;
      }
      setEditing(false);
    };
    const onScroll = (e) => {
      const menu = menuRef.current;
      if (menu && e.target instanceof Node && menu.contains(e.target)) return;
      if (
        e.target?.closest?.(
          ".react-datepicker, .react-datepicker-popper, .custom-dropdown, .dropdown-menu, .pm-id-guide-modal",
        )
      ) {
        return;
      }
      // Keep open while the calendar month/year dropdown is active.
      if (
        document.querySelector(".react-datepicker-popper") ||
        document.querySelector(".react-datepicker__portal")
      ) {
        return;
      }
      setEditing(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [editing]);

  const shown = displayValue ?? displayOrDash(value);

  const openEditor = (event) => {
    event?.stopPropagation?.();
    if (resolvedEditor === "phone") {
      setDraft(sanitizeNationalMobileInput(value, countryIso2));
    } else {
      setDraft(String(value ?? ""));
    }
    setDraftSecondary(String(secondaryValue ?? ""));
    setDraftDate(utils.parseDateInput(value));
    setMenuPos(
      placePopupNearAnchor(wrapRef.current, {
        width: menuWidth,
        height: menuHeight,
      }),
    );
    setEditing(true);
  };

  const commit = async () => {
    if (!onSave) {
      setEditing(false);
      return;
    }
    let nextValue = draft;
    if (resolvedEditor === "date") {
      nextValue = formatDobForSave(draftDate);
    } else if (resolvedEditor === "gender") {
      nextValue = toGenderLabel(draft);
    } else if (resolvedEditor === "phone") {
      nextValue = sanitizeNationalMobileInput(draft, countryIso2);
    }
    if (
      secondaryWhenValue != null &&
      String(nextValue).toLowerCase() ===
        String(secondaryWhenValue).toLowerCase() &&
      !String(draftSecondary || "").trim()
    ) {
      return;
    }
    setSaving(true);
    try {
      const result = await onSave(nextValue, {
        secondary: String(draftSecondary || "").trim(),
      });
      // Explicit false = validation aborted; keep editor open.
      if (result !== false) setEditing(false);
    } catch {
      // Keep editor open on unexpected save failures (alert shown by caller).
    } finally {
      setSaving(false);
    }
  };

  const selectedOption =
    selectOptions.find(
      (o) => String(o.value).toLowerCase() === String(draft).toLowerCase(),
    ) || null;

  return (
    <div className="pm-field">
      <div className="pm-field__label">{label}</div>
      <div className="pm-field__value-row" ref={wrapRef}>
        <div className="pm-field__value">{shown}</div>
        {editable ? (
          <button
            type="button"
            className="pm-field__edit"
            onClick={(e) => (editing ? setEditing(false) : openEditor(e))}
            title={`Edit ${accessibleLabel}`}
            aria-label={`Edit ${accessibleLabel}`}
            aria-expanded={editing}
          >
            <i className="fas fa-pencil-alt" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {editing && editable
        ? createPortal(
            <div
              ref={menuRef}
              className="pm-inline-edit__popover"
              role="dialog"
              aria-label={`Edit ${accessibleLabel}`}
              style={{
                top: menuPos.top,
                left: menuPos.left,
                width: menuWidth,
              }}
              onClick={(e) => e.stopPropagation()}
              data-pm-inline-popover="1"
            >
              <div className="pm-inline-edit__title">
                Edit {accessibleLabel}
              </div>
              <label className="pm-inline-edit__label">
                {label}
                {resolvedEditor === "gender" ? (
                  <GenderSelector
                    value={draft || null}
                    size="small"
                    disabled={saving}
                    onChange={(next) => setDraft(toGenderLabel(next))}
                  />
                ) : resolvedEditor === "date" ? (
                  <DatePicker
                    selected={draftDate}
                    onChange={(date) => setDraftDate(date)}
                    placeholderText="Select date"
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    onChangeRaw={(e) => e.preventDefault()}
                    popperProps={{ strategy: "fixed" }}
                    disabled={saving}
                  />
                ) : resolvedEditor === "select" ? (
                  <>
                    <Dropdown
                      options={selectOptions}
                      selected={selectedOption}
                      onSelect={(opt) => {
                        const next = opt?.value || "";
                        setDraft(next);
                        if (
                          secondaryWhenValue == null ||
                          String(next).toLowerCase() !==
                            String(secondaryWhenValue).toLowerCase()
                        ) {
                          setDraftSecondary("");
                        }
                      }}
                      showSearch
                      disabled={saving}
                    />
                    {showSecondary ? (
                      <input
                        type="text"
                        className="form-control form-control-sm mt-2"
                        value={draftSecondary}
                        onChange={(e) => setDraftSecondary(e.target.value)}
                        placeholder={
                          secondaryPlaceholder || secondaryLabel || "Other"
                        }
                        disabled={saving}
                        aria-label={secondaryLabel || "Other"}
                      />
                    ) : null}
                  </>
                ) : resolvedEditor === "textarea" ? (
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={saving}
                    autoFocus
                  />
                ) : resolvedEditor === "phone" ? (
                  <div className="pm-phone-input">
                    <select
                      className="form-control form-control-sm pm-phone-input__dial"
                      value={
                        dialLabel ||
                        orgMobileDialLabel(countryIso2)
                      }
                      disabled
                      aria-label="Organisation country dial code"
                    >
                      <option
                        value={
                          dialLabel ||
                          orgMobileDialLabel(countryIso2)
                        }
                      >
                        {dialLabel || orgMobileDialLabel(countryIso2)}
                      </option>
                    </select>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={draft}
                      onChange={(e) => {
                        const { national } = normalizeNationalMobileInput(
                          e.target.value,
                          countryIso2,
                        );
                        setDraft(national);
                      }}
                      onPaste={(e) => {
                        const text = e.clipboardData?.getData("text") ?? "";
                        e.preventDefault();
                        const { national } = normalizeNationalMobileInput(
                          text,
                          countryIso2,
                        );
                        setDraft(national);
                      }}
                      disabled={saving}
                      autoFocus
                      inputMode="numeric"
                      placeholder="7123456789"
                    />
                  </div>
                ) : (
                  <input
                    type={inputType}
                    className="form-control form-control-sm"
                    value={draft}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDraft(
                        typeof formatDraft === "function"
                          ? formatDraft(next)
                          : next,
                      );
                    }}
                    disabled={saving}
                    autoFocus
                    maxLength={inputMaxLength}
                    inputMode={inputMode}
                  />
                )}
              </label>
              <div className="pm-inline-edit__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={commit}
                  disabled={
                    saving ||
                    (showSecondary && !String(draftSecondary || "").trim())
                  }
                >
                  Save
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
function ProfileDetailsPanel({
  volunteer,
  onFieldSave,
  onSaveNhsNumber,
  onSaveNiNumber,
  canEdit,
  ethnicityOptions = [],
  orgCountryIso2 = "GB",
}) {
  const mobileCountryIso2 = String(
    volunteer?.MobileCountryIso2 || orgCountryIso2 || "GB",
  )
    .trim()
    .toUpperCase();
  // onFieldSave(patch, options?) - options may include confirmRemoveFromDb
  if (!volunteer) {
    return <div className="pm-empty-state">Participant not found.</div>;
  }

  const nhsDisplay = getNhsDisplayWithVerify(
    volunteer.NHSNumber,
    volunteer.IsVerifyNHS,
  );
  const niDisplay = getNiDisplayWithVerify(
    volunteer.NINumber,
    volunteer.IsVerify,
  );

  const firstName = volunteer.FirstName || volunteer.ForeName || "";
  const lastName = volunteer.LastName || volunteer.SurName || "";
  const genderRaw = String(volunteer.Gender ?? "").trim();
  const ageYears = getParticipantAgeYears(volunteer.DOB);
  const ageLabel = ageYears == null ? "-" : `${ageYears} years`;
  const ethnicityId = String(
    volunteer.Ethnicity ?? volunteer.ethnicity ?? "",
  ).trim();
  const ethnicityLabel =
    ethnicityOptions.find(
      (o) => String(o.value).toLowerCase() === ethnicityId.toLowerCase(),
    )?.label ||
    volunteer.EthnicityName ||
    volunteer.ethnicityName ||
    "";

  return (
    <div className="pm-details-stack">
      <div className="pm-field-grid">
        <EditableField
          label="First name"
          value={firstName}
          editable={canEdit}
          onSave={(v) => onFieldSave({ ForeName: v, FirstName: v })}
        />
        <EditableField
          label="Last name"
          value={lastName}
          editable={canEdit}
          onSave={(v) => onFieldSave({ SurName: v, LastName: v })}
        />

        <EditableField
          label="Date of birth"
          value={volunteer.DOB || ""}
          displayValue={formatParticipantDate(volunteer.DOB)}
          editor="date"
          editable={canEdit}
          onSave={(v) => {
            const years = getParticipantAgeYears(v);
            onFieldSave({
              DOB: v,
              ...(years == null ? {} : { Age: years }),
            });
          }}
        />
        <EditableField label="Age" value={ageLabel} editable={false} />
        <EditableField
          label="Gender"
          value={genderRaw}
          displayValue={toGenderLabel(genderRaw) || "Not recorded"}
          editor="gender"
          editable={canEdit}
          onSave={(v) => onFieldSave({ Gender: v })}
        />
        <EditableField
          label="Ethnicity"
          value={ethnicityId}
          displayValue={ethnicityLabel || "Not recorded"}
          editor="select"
          options={ethnicityOptions}
          editable={canEdit}
          secondaryWhenValue="AddNew"
          secondaryLabel="Other Ethnicity"
          secondaryPlaceholder="Other Ethnicity"
          secondaryValue={
            volunteer.EthnicityReason || volunteer.ethnicityReason || ""
          }
          onSave={(v, extra) =>
            onFieldSave({
              Ethnicity: v,
              EthnicityReason:
                String(v).toLowerCase() === "addnew"
                  ? extra?.secondary || ""
                  : "",
            })
          }
        />

        <EditableField
          label="Mobile"
          value={volunteer.ContactNumber || ""}
          displayValue={
            volunteer.ContactNumber
              ? `${orgMobileDialLabel(mobileCountryIso2)} ${sanitizeNationalMobileInput(volunteer.ContactNumber, mobileCountryIso2) || volunteer.ContactNumber}`
              : undefined
          }
          editor="phone"
          dialPrefix={dialCodeForCountry(mobileCountryIso2)}
          dialLabel={orgMobileDialLabel(mobileCountryIso2)}
          countryIso2={mobileCountryIso2}
          editable={canEdit}
          onSave={(v) =>
            onFieldSave({
              ContactNumber: v,
              MobileCountryIso2: mobileCountryIso2,
            })
          }
        />
        <EditableField
          label="Email"
          value={volunteer.EmailId || ""}
          inputType="email"
          editable={canEdit}
          onSave={(v) => onFieldSave({ EmailId: v })}
        />
        <EditableField
          label="Work email"
          value={volunteer.PEmailId || volunteer.WorkEmailId || ""}
          inputType="email"
          editable={canEdit}
          onSave={(v) => onFieldSave({ WorkEmailId: v, PEmailId: v })}
        />
      </div>
      <div className="pm-details-section">
        <h3 className="pm-details-section__title">Contact &amp; identifiers</h3>
        <div className="pm-field-grid">
          <EditableField
            label="Address line"
            value={volunteer.AddressLine1 || ""}
            editor="textarea"
            editable={canEdit}
            onSave={(v) => onFieldSave({ AddressLine1: v })}
          />
          <EditableField
            label="City"
            value={volunteer.City || ""}
            editable={canEdit}
            onSave={(v) => onFieldSave({ City: v })}
          />
          <EditableField
            label="Post code"
            value={volunteer.PostCode || ""}
            editable={canEdit}
            onSave={(v) => onFieldSave({ PostCode: v })}
          />
          <EditableField
            label="Country"
            value={volunteer.Country || ""}
            editable={canEdit}
            onSave={(v) => onFieldSave({ Country: v })}
          />
          <EditableField
            label={
              <IdNumberLabel kind={ID_NUMBER_KINDS.NHS}>NHS number</IdNumberLabel>
            }
            labelText="NHS number"
            value={formatNhsForDisplay(volunteer.NHSNumber || "")}
            displayValue={nhsDisplay}
            editable={canEdit}
            formatDraft={formatNhsForDisplay}
            inputMaxLength={12}
            inputMode="numeric"
            onSave={(v) =>
              onSaveNhsNumber
                ? onSaveNhsNumber(v)
                : onFieldSave({ NHSNumber: v })
            }
          />
          <EditableField
            label={
              <IdNumberLabel kind={ID_NUMBER_KINDS.NI}>NI number</IdNumberLabel>
            }
            labelText="NI number"
            value={formatNiForDisplay(volunteer.NINumber || "")}
            displayValue={niDisplay}
            editable={canEdit}
            formatDraft={formatNiForDisplay}
            inputMaxLength={13}
            onSave={(v) =>
              onSaveNiNumber
                ? onSaveNiNumber(v)
                : onFieldSave({ NINumber: v })
            }
          />
          <EditableField
            label="Next of kin"
            value={volunteer.KinName || volunteer.NextofKinName || ""}
            editable={canEdit}
            onSave={(v) => onFieldSave({ NextofKinName: v, KinName: v })}
          />
          <EditableField
            label="Kin contact"
            value={volunteer.KinContactNo || volunteer.NextofKinContact || ""}
            editable={canEdit}
            onSave={(v) =>
              onFieldSave({ NextofKinContact: v, KinContactNo: v })
            }
          />
        </div>
      </div>

      <div className="pm-details-section">
        <h3 className="pm-details-section__title">Contact preferences</h3>
        <p className="pm-details-section__lead">
          Choose how this participant can be contacted for study communications.
        </p>
        <div className="pm-contact-prefs" role="group" aria-label="Contact preferences">
          {[
            { key: "IsEmail", label: "Receive Email" },
            { key: "IsSMS", label: "Receive SMS" },
            { key: "IsPost", label: "Receive Post" },
          ].map(({ key, label }) => {
            const checked = coerceVolunteerPrefFlag(volunteer[key], false);
            if (!canEdit) {
              return (
                <div key={key} className="pm-contact-prefs__item is-readonly">
                  <span className="pm-contact-prefs__label">{label}</span>
                  <span
                    className={`pm-contact-prefs__value${
                      checked ? " is-on" : ""
                    }`}
                  >
                    {checked ? "Yes" : "No"}
                  </span>
                </div>
              );
            }
            return (
              <label key={key} className="pm-contact-prefs__item">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={checked}
                  onChange={(event) => {
                    const next = event.target.checked;
                    onFieldSave(
                      { [key]: next },
                      {
                        successMessage: `${label} updated.`,
                      },
                    );
                  }}
                />
                <span className="pm-contact-prefs__label">{label}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function parseChangedFields(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(String(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatChangeValue(field) {
  if (!field || typeof field !== "object") return { oldValue: "—", newValue: "—" };
  if (String(field.fieldType || "").toLowerCase() === "checkbox") {
    return {
      oldValue: field.oldValue ? "Yes" : "No",
      newValue: field.newValue ? "Yes" : "No",
    };
  }
  const oldValue = field.oldValue ?? field.OldValue ?? "";
  const newValue = field.newValue ?? field.NewValue ?? "";
  return {
    oldValue: String(oldValue).trim() || "—",
    newValue: String(newValue).trim() || "—",
  };
}

function getAuditRowMeta(row) {
  const action = row.ActionType || row.actionType || row.Action || "Update";
  const when = row.ActionDate || row.actionDate || row.CreatedOn || "";
  const by = row.ActionBy || row.actionBy || row.CreatedBy || "—";
  const email = row.EmailId || row.emailId || row.Email || "";
  const reason = row.Reason || row.reason || "";
  const changes = parseChangedFields(
    row.ChangedFormFields ?? row.changedFormFields ?? "",
  );
  const actionLower = String(action).toLowerCase();
  // Clinical / appointment style actions already describe the event.
  const actionIsDescriptive =
    /\b(added|updated|deleted|cancelled|rescheduled|created|sent)\b/i.test(
      actionLower,
    );
  let title = actionIsDescriptive
    ? action
    : changes.length === 1
      ? `Updated ${changes[0].label || changes[0].Label || "field"}`
      : changes.length > 1
        ? `Updated ${changes.length} fields`
        : action;
  // Update rows: keep action title, but surface how many values changed.
  if (
    actionIsDescriptive &&
    /\bupdated\b/i.test(actionLower) &&
    changes.length > 0
  ) {
    title = `${action} · ${changes.length} field${
      changes.length === 1 ? "" : "s"
    }`;
  }
  return { action, when, by, email, reason, changes, title };
}

function startOfDay(date) {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfDay(date) {
  if (!date) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );
}

function isAuditDateInRange(when, startDate, endDate) {
  if (!startDate && !endDate) return true;
  const date = utils.parseDateInput(when);
  if (!date) return false;
  if (startDate && date < startOfDay(startDate)) return false;
  if (endDate && date > endOfDay(endDate)) return false;
  return true;
}

function AuditLogPanel({ rows }) {
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState([]);
  const [actorFilter, setActorFilter] = useState([]);
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [fieldChangesOnly, setFieldChangesOnly] = useState(false);

  const actionOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((row) => {
      const action = String(
        row.ActionType || row.actionType || row.Action || "",
      ).trim();
      if (action) set.add(action);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [rows]);

  const actorOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((row) => {
      const by = String(
        row.ActionBy || row.actionBy || row.CreatedBy || "",
      ).trim();
      if (by && by !== "—") set.add(by);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [rows]);

  const selectedActions = useMemo(
    () =>
      Array.isArray(actionFilter)
        ? actionFilter.map((item) =>
            typeof item === "string" ? item : item?.value,
          ).filter(Boolean)
        : [],
    [actionFilter],
  );

  const selectedActors = useMemo(
    () =>
      Array.isArray(actorFilter)
        ? actorFilter.map((item) =>
            typeof item === "string" ? item : item?.value,
          ).filter(Boolean)
        : [],
    [actorFilter],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter((row) => {
      const meta = getAuditRowMeta(row);
      if (
        selectedActions.length &&
        !selectedActions.includes(meta.action)
      ) {
        return false;
      }
      if (selectedActors.length && !selectedActors.includes(meta.by)) {
        return false;
      }
      if (fieldChangesOnly && !meta.changes.length) return false;
      if (!isAuditDateInRange(meta.when, dateStart, dateEnd)) return false;
      if (!q) return true;
      const haystack = [
        meta.title,
        meta.action,
        meta.by,
        meta.email,
        meta.reason,
        meta.when,
        ...meta.changes.map(
          (c) =>
            `${c.label || ""} ${c.oldValue ?? ""} ${c.newValue ?? ""}`,
        ),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [
    dateEnd,
    dateStart,
    fieldChangesOnly,
    rows,
    search,
    selectedActions,
    selectedActors,
  ]);

  const hasFilters =
    Boolean(search.trim()) ||
    selectedActions.length > 0 ||
    selectedActors.length > 0 ||
    Boolean(dateStart) ||
    Boolean(dateEnd) ||
    fieldChangesOnly;

  const clearFilters = () => {
    setSearch("");
    setActionFilter([]);
    setActorFilter([]);
    setDateStart(null);
    setDateEnd(null);
    setFieldChangesOnly(false);
  };

  if (!rows?.length) {
    return (
      <div className="pm-empty-state">
        No audit log entries yet for this participant.
      </div>
    );
  }

  return (
    <div className="pm-audit-log">
      <div className="pm-audit-log__main">
        <div className="pm-audit-log__summary">
          Showing {filteredRows.length} of {rows.length} entries
        </div>
        {filteredRows.length ? (
          <ol className="pm-audit-log__list pm-thin-scroll">
            {filteredRows.map((row, index) => {
              const { action, when, by, email, reason, changes, title } =
                getAuditRowMeta(row);
              const key = String(
                row.VolunteerActivityLogId ??
                  row.ActivityLogId ??
                  row.Id ??
                  `${when}-${action}-${index}`,
              );

              return (
                <li
                  key={key}
                  className={`pm-audit-log__item${
                    changes.length ? "" : " pm-audit-log__item--simple"
                  }`}
                >
                  <div className="pm-audit-log__rail" aria-hidden="true">
                    <span className="pm-audit-log__dot" />
                    {index < filteredRows.length - 1 ? (
                      <span className="pm-audit-log__line" />
                    ) : null}
                  </div>
                  <article className="pm-audit-log__card">
                    <header className="pm-audit-log__head">
                      <div className="pm-audit-log__title-wrap">
                        <h3 className="pm-audit-log__title">{title}</h3>
                        {when ? (
                          <time className="pm-audit-log__when" dateTime={when}>
                            {when}
                          </time>
                        ) : null}
                        {changes.length && action && title !== action ? (
                          <span className="pm-audit-log__action-type">
                            {action}
                          </span>
                        ) : null}
                      </div>
                      <div className="pm-audit-log__meta">
                        <span className="pm-audit-log__meta-item">
                          <i className="fas fa-user" aria-hidden="true" />
                          {by}
                        </span>
                        {email ? (
                          <span className="pm-audit-log__meta-item">
                            <i className="fas fa-envelope" aria-hidden="true" />
                            {email}
                          </span>
                        ) : null}
                      </div>
                    </header>

                    {reason ? (
                      <p className="pm-audit-log__reason">
                        <span>Reason</span> {reason}
                      </p>
                    ) : null}

                    {changes.length ? (
                      <ul className="pm-audit-log__changes">
                        {changes.map((field, fieldIndex) => {
                          const label =
                            field.label ||
                            field.Label ||
                            field.field ||
                            "Field";
                          const { oldValue, newValue } =
                            formatChangeValue(field);
                          return (
                            <li
                              key={`${key}-f-${fieldIndex}`}
                              className="pm-audit-log__change"
                            >
                              <span className="pm-audit-log__change-label">
                                {label}
                              </span>
                              <span
                                className="pm-audit-log__value pm-audit-log__value--old"
                                title="Previous value"
                              >
                                {oldValue}
                              </span>
                              <i
                                className="fas fa-arrow-right"
                                aria-hidden="true"
                              />
                              <span
                                className="pm-audit-log__value pm-audit-log__value--new"
                                title="New value"
                              >
                                {newValue}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </article>
                </li>
              );
            })}
          </ol>
        ) : (
          <div className="pm-empty-state pm-audit-log__empty-filter">
            No entries match the current filters.
            {hasFilters ? (
              <button
                type="button"
                className="btn btn-link btn-sm px-0"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        )}
      </div>

      <aside
        className="pm-audit-filters pm-thin-scroll"
        aria-label="Audit log filters"
      >
        <div className="pm-audit-filters__head">
          <h3 className="pm-audit-filters__title">Filters</h3>
          {hasFilters ? (
            <button
              type="button"
              className="btn btn-link btn-sm px-0"
              onClick={clearFilters}
            >
              Reset
            </button>
          ) : null}
        </div>

        <label className="pm-audit-filters__field">
          <span>Search</span>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Search action, user, field…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <div className="pm-audit-filters__field">
          <span>Date range</span>
          <DatePicker
            selectsRange
            startDate={dateStart}
            endDate={dateEnd}
            onChange={(dates) => {
              const [start, end] = dates || [];
              setDateStart(start || null);
              setDateEnd(end || null);
            }}
            isClearable
            placeholderText="From date – To date"
            className="form-control form-control-sm"
            dateFormat="dd/MM/yyyy"
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            popperProps={{ strategy: "fixed" }}
            maxDate={new Date()}
          />
        </div>

        <div className="pm-audit-filters__field">
          <span>Action type</span>
          <Dropdown
            options={actionOptions}
            isMulti
            selected={actionFilter}
            onSelect={setActionFilter}
            showSearch
            showMultiSelectedTooltip
          />
        </div>

        <div className="pm-audit-filters__field">
          <span>Updated by</span>
          <Dropdown
            options={actorOptions}
            isMulti
            selected={actorFilter}
            onSelect={setActorFilter}
            showSearch
            showMultiSelectedTooltip
          />
        </div>

        <label className="pm-audit-filters__check">
          <input
            type="checkbox"
            checked={fieldChangesOnly}
            onChange={(e) => setFieldChangesOnly(e.target.checked)}
          />
          <span>Only entries with field changes</span>
        </label>
      </aside>
    </div>
  );
}

function AttendanceSidebarCard({ appointmentInfo }) {
  const info = appointmentInfo || {};
  const total = Number(info.Appointments ?? info.appointments ?? 0) || 0;
  const attendanceRaw = info.Attendance ?? info.attendance ?? 0;
  const attendancePct = Math.max(
    0,
    Math.min(100, Number(String(attendanceRaw).replace("%", "")) || 0),
  );
  const confirmed = info.Confirmed ?? info.confirmed ?? 0;
  const cancelled = info.Cancelled ?? info.cancelled ?? 0;
  const dna = info.NotAttempt ?? info.DidNotAttend ?? info.notAttempt ?? 0;
  const lastStudy = String(info.StudyName ?? info.studyName ?? "").trim();
  const lastTime = String(
    info.AppointmentTime ?? info.appointmentTime ?? "",
  ).trim();
  const nextStudy = String(
    info.StudyNameNext ?? info.studyNameNext ?? "",
  ).trim();
  const nextTime = String(
    info.AppointmentTimeNext ?? info.appointmentTimeNext ?? "",
  ).trim();
  const lastLabel =
    lastStudy || lastTime
      ? [lastStudy, lastTime].filter(Boolean).join(" · ")
      : "None yet.";
  const nextLabel =
    nextStudy || nextTime
      ? [nextStudy, nextTime].filter(Boolean).join(" · ")
      : "—";

  return (
    <section className="pm-side-card">
      <h3 className="pm-side-card__title">Attendance</h3>
      <p className="pm-side-card__lead">
        {total
          ? `${total} appointment${total === 1 ? "" : "s"} · ${attendancePct}% attendance`
          : "No appointment history yet"}
      </p>
      <div className="pm-side-card__bar" aria-hidden="true">
        <span style={{ width: `${attendancePct}%` }} />
      </div>
      <ul className="pm-side-card__list">
        <li>
          <span className="pm-dot pm-dot--ok" /> Confirmed{" "}
          <strong>{confirmed}</strong>
        </li>
        <li>
          <span className="pm-dot pm-dot--warn" /> Cancelled{" "}
          <strong>{cancelled}</strong>
        </li>
        <li>
          <span className="pm-dot pm-dot--danger" /> Did not attend{" "}
          <strong>{dna}</strong>
        </li>
      </ul>
      <p className="pm-side-card__muted">Last appointment: {lastLabel}</p>
      <div className="pm-side-card__footer">
        <div className="pm-side-card__footer-label">Next appointment</div>
        <div className="pm-side-card__footer-value">{nextLabel}</div>
      </div>
    </section>
  );
}

function StudiesSidebarCard({ studyCounts }) {
  return (
    <section className="pm-side-card">
      <h3 className="pm-side-card__title">Studies</h3>
      <ul className="pm-side-card__stats">
        <li>
          <span>Studies</span>
          <strong>{studyCounts.total}</strong>
        </li>
        <li>
          <span>Live studies</span>
          <strong>{studyCounts.live}</strong>
        </li>
        <li>
          <span>Archived</span>
          <strong>{studyCounts.archived}</strong>
        </li>
      </ul>
    </section>
  );
}

function HeroStatusControl({ volunteer, onStatusChange, readOnly = false }) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const locked =
    readOnly ||
    isVolunteerArchived(volunteer) ||
    isVolunteerProfileReadOnly(volunteer);
  const current = volStatusToCode(volunteer?.VolStatus);
  const label = getVolunteerStatusLabel(volunteer?.VolStatus);
  const style = getVolunteerStatusPillStyle(volunteer?.VolStatus);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!label || label === "—") return null;

  if (locked) {
    return (
      <span
        className="pm-status-pill pm-status-pill--readonly"
        style={style}
        title="Status can be changed from the participant list"
      >
        {label}
      </span>
    );
  }

  return (
    <div className="pm-status-cell" ref={wrapRef}>
      <button
        type="button"
        className="pm-status-pill"
        style={style}
        onClick={(event) => {
          event.stopPropagation();
          setMenuPos(
            placeMenuNearAnchor(wrapRef.current, { width: 170, height: 180 }),
          );
          setOpen((prev) => !prev);
        }}
        title={`${label} — Change status`}
      >
        <span>{label}</span>
        <i className="fas fa-pencil-alt" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="pm-status-menu"
              role="listbox"
              aria-label="Change participant status"
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="pm-status-menu__title">Change status</div>
              {PM_VOL_STATUS_OPTIONS.map((option) => {
                const selected = option.value === current;
                const optionStyle = getVolunteerStatusPillStyle(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={[
                      "pm-status-menu__item",
                      selected ? "is-selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setOpen(false);
                      if (!selected) onStatusChange(option.value);
                    }}
                  >
                    <span
                      className="pm-status-menu__dot"
                      style={{ background: optionStyle.color }}
                    />
                    {option.label}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

const ParticipantProfile = () => {
  const { volunteerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const {
    canViewParticipantProfile,
    canAddParticipant,
    loading: accessLoading,
  } = useParticipantAccess();

  const [volunteer, setVolunteer] = useState(null);
  const [orgCountryIso2, setOrgCountryIso2] = useState("GB");
  const [notes, setNotes] = useState([]);
  const [activityRows, setActivityRows] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [studyCounts, setStudyCounts] = useState({
    total: 0,
    live: 0,
    archived: 0,
  });
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [showCodes, setShowCodes] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState({
    open: false,
    nextStatus: null,
  });
  const [dbConfirm, setDbConfirm] = useState({ open: false, nextOnList: null });
  const {
    VerifyModal: IdVerifyModal,
    ensureNhsVerified,
    ensureNiVerified,
  } = useIdVerifyPrompt();
  const [addOpen, setAddOpen] = useState(false);
  const [ethnicityOptions, setEthnicityOptions] = useState([]);
  const fileInputRef = useRef(null);

  const basePath = `/participantmanager/participant/${volunteerId}`;
  const profileReadOnly = isVolunteerProfileReadOnly(volunteer);
  const canEdit = Boolean(canAddParticipant) && !profileReadOnly;

  const loadEthnicityOptions = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    try {
      const { ethnicities } = await fetchEthnicityList(userData);
      const { options } = mapEthnicityOptions(ethnicities, {
        includeAddNew: true,
      });
      setEthnicityOptions(options);
    } catch {
      setEthnicityOptions([]);
    }
  }, [userData]);

  const loadSideData = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    try {
      let summary = await fetchVolunteerStudySummary(userData, volunteerId);
      let docs = Array.isArray(summary.documents) ? summary.documents : [];
      // Legacy uploads stored blobHost + data:base64 — OnlyOffice cannot preview those.
      // Repair once to real blob URLs (same shape as QMS / staff documents).
      if (docs.some((d) => isBrokenVolunteerDocumentUrl(d?.DocumentUrl || d?.documentUrl))) {
        try {
          await repairVolunteerDocuments(userData, volunteerId);
          summary = await fetchVolunteerStudySummary(userData, volunteerId);
          docs = Array.isArray(summary.documents) ? summary.documents : [];
        } catch (repairErr) {
          console.warn("Volunteer document URL repair failed:", repairErr);
        }
      }
      setDocuments(docs);
      setAppointmentInfo(summary.appointmentInfo || null);
      const counts = summary.studyCounts || {};
      setStudyCounts({
        total: Number(counts.total) || 0,
        live: Number(counts.live) || 0,
        archived: Number(counts.archived) || 0,
      });
    } catch {
      setDocuments([]);
      setAppointmentInfo(null);
      setStudyCounts({ total: 0, live: 0, archived: 0 });
    }
  }, [userData, volunteerId]);

  const refreshActivityLog = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    const activityLogRows = await fetchVolunteerActivityLog(
      userData,
      volunteerId,
    ).catch(() => []);
    setActivityRows(activityLogRows);
  }, [userData, volunteerId]);

  const loadProfile = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    showLoader("Loading participant profile...");
    try {
      const [{ volunteer: volRow }, orgResponse] = await Promise.all([
        fetchVolunteerProfile(userData, volunteerId),
        fetchOrganizationSetup(userData.OrganizationId, userData.StaffId).catch(
          () => null,
        ),
      ]);
      if (orgResponse && Number(orgResponse?.success) === 1) {
        const { setupRow } = parseOrganizationSetupResponse(orgResponse);
        setOrgCountryIso2(setupRow?.CountryIso2 || "GB");
      }
      setVolunteer(volRow);
      setPhotoFailed(false);
      const [noteRows, activityLogRows] = await Promise.all([
        fetchVolunteerNotes(userData, volunteerId).catch(() => []),
        fetchVolunteerActivityLog(userData, volunteerId).catch(() => []),
      ]);
      setNotes(noteRows);
      setActivityRows(activityLogRows);
      await loadSideData();
    } catch (err) {
      showAlert({
        message: err?.message || "Could not load participant profile.",
      });
    } finally {
      hideLoader();
    }
  }, [
    hideLoader,
    loadSideData,
    showAlert,
    showLoader,
    userData,
    volunteerId,
  ]);

  const isOnParticipantDb = useMemo(
    () => isParticipantOnDb(volunteer?.IsUseVolunteerDB),
    [volunteer?.IsUseVolunteerDB],
  );

  const visibleTabs = useMemo(
    () =>
      PROFILE_TABS.filter(
        (tab) => !tab.requiresParticipantDb || isOnParticipantDb,
      ),
    [isOnParticipantDb],
  );

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadEthnicityOptions();
  }, [loadEthnicityOptions]);

  // Clinical only applies when participant is on the Participant DB.
  // Legacy /medication URL redirects into Clinical → Medication section.
  useEffect(() => {
    if (!volunteerId) return;
    const path = String(location.pathname || "");
    if (path.endsWith("/medication")) {
      navigate(`/participantmanager/participant/${volunteerId}/clinical`, {
        replace: true,
        state: { clinicalSection: "medication" },
      });
      return;
    }
    if (!isOnParticipantDb && path.endsWith("/clinical")) {
      navigate(`/participantmanager/participant/${volunteerId}`, {
        replace: true,
      });
    }
  }, [isOnParticipantDb, location.pathname, navigate, volunteerId]);

  const displayName = useMemo(
    () => getVolunteerDisplayName(volunteer) || "Participant",
    [volunteer],
  );

  const photoUrl = getParticipantProfilePictureUrl(volunteer);
  const showPhoto = isUsablePhotoUrl(photoUrl) && !photoFailed;
  const initials = getInitials(volunteer, displayName);
  const ageYears = getParticipantAgeYears(volunteer?.DOB);
  const genderRaw = String(volunteer?.Gender ?? "").trim();
  const genderMeta = toGenderLabel(genderRaw) || "Gender not recorded";

  const metaBits = [
    volunteer?.VolunteerNo || volunteerId,
    genderMeta,
    ageYears == null ? null : `${ageYears} years`,
  ].filter(Boolean);

  const saveVolunteerPatch = useCallback(
    async (patch, { successMessage, confirmRemoveFromDb } = {}) => {
      if (!volunteer || !userData?.OrganizationId) return;
      if (isVolunteerProfileReadOnly(volunteer)) {
        showAlert({
          message:
            "This participant is inactive or removed. Reactivate from the list to edit.",
        });
        return;
      }

      if (confirmRemoveFromDb) {
        setDbConfirm({ open: true, nextOnList: false });
        return;
      }

      let nextPatch = { ...patch };
      if (Object.prototype.hasOwnProperty.call(nextPatch, "ContactNumber")) {
        const mobileCountryIso2 = String(
          nextPatch.MobileCountryIso2 ||
            volunteer.MobileCountryIso2 ||
            orgCountryIso2 ||
            "GB",
        )
          .trim()
          .toUpperCase();
        const phoneResult = validateNationalMobile(nextPatch.ContactNumber, {
          countryIso2: mobileCountryIso2,
          required: true,
        });
        if (!phoneResult.valid) {
          showAlert({ message: phoneResult.message });
          return false;
        }
        nextPatch.ContactNumber = phoneResult.national;
        nextPatch.MobileCountryIso2 = mobileCountryIso2;
      }

      const payload = buildVolunteerUpdatePayload(volunteer, nextPatch);
      if (!payload.VolunteerDemRecordId) {
        payload.VolunteerDemRecordId = volunteerId;
      }
      showLoader("Saving participant...");
      try {
        await updateVolunteer(userData, payload);
        setVolunteer((prev) => ({ ...(prev || {}), ...nextPatch }));
        showAlert({
          message: successMessage || "Participant details updated.",
          type: "success",
        });
        await loadProfile();
      } catch (err) {
        showAlert({
          message: err?.message || "Could not update participant.",
        });
        throw err;
      } finally {
        hideLoader();
      }
    },
    [
      hideLoader,
      loadProfile,
      orgCountryIso2,
      showAlert,
      showLoader,
      userData,
      volunteer,
      volunteerId,
    ],
  );

  const saveNhsNumber = useCallback(
    async (value) => {
      const digits = sanitizeNhsNumberInput(value);
      if (!digits) {
        await saveVolunteerPatch({ NHSNumber: "", IsVerifyNHS: 0 });
        return;
      }
      const result = await ensureNhsVerified(value, 0);
      if (!result.ok) {
        showAlert({ message: result.message });
        return false;
      }
      await saveVolunteerPatch({
        NHSNumber: result.digits,
        IsVerifyNHS: result.IsVerifyNHS,
      });
    },
    [ensureNhsVerified, saveVolunteerPatch, showAlert],
  );

  const saveNiNumber = useCallback(
    async (value) => {
      const raw = String(value ?? "").trim();
      if (!raw) {
        await saveVolunteerPatch({ NINumber: "", IsVerify: 0 });
        return;
      }
      const result = await ensureNiVerified(value, 0);
      if (!result.ok) {
        showAlert({ message: result.message });
        return false;
      }
      await saveVolunteerPatch({
        NINumber: result.ni,
        IsVerify: result.IsVerify,
      });
    },
    [ensureNiVerified, saveVolunteerPatch, showAlert],
  );

  const applyStatusChange = useCallback(
    async (nextStatus) => {
      const id = getVolunteerRowId(volunteer) || volunteerId;
      if (!id) return;
      showLoader("Updating participant status...");
      try {
        await changeVolunteerStatus(userData, id, Number(nextStatus));
        showAlert({
          message:
            Number(nextStatus) === 1
              ? "Participant set to Inactive."
              : Number(nextStatus) === 2
                ? "Participant removed."
                : "Participant set to Active.",
          type: "success",
        });
        await loadProfile();
      } catch (err) {
        // Status may still have saved (e.g. Remove + app de-link side-effect). Refresh.
        await loadProfile().catch(() => {});
        showAlert({ message: err?.message || "Could not update status." });
      } finally {
        hideLoader();
      }
    },
    [hideLoader, loadProfile, showAlert, showLoader, userData, volunteer, volunteerId],
  );

  const requestStatusChange = (nextStatus) => {
    if (profileReadOnly) return;
    const statusCode = Number(nextStatus);
    const currentCode = volStatusToCode(volunteer?.VolStatus);
    if (statusCode === currentCode) return;

    // Confirm before Inactivate / Remove — SP blocks if Confirmed appointments exist.
    if (statusCode === 1 || statusCode === 2) {
      setStatusConfirm({ open: true, nextStatus: statusCode });
      return;
    }

    applyStatusChange(statusCode);
  };

  const requestDbToggle = () => {
    if (!canEdit) return;
    setDbConfirm({ open: true, nextOnList: !isOnParticipantDb });
  };

  const confirmDbChange = async () => {
    const nextOnList = dbConfirm.nextOnList;
    setDbConfirm({ open: false, nextOnList: null });
    if (nextOnList == null) return;
    if (nextOnList) {
      await saveVolunteerPatch(
        { IsVolunteerDatabase: 1, IsUseVolunteerDB: "Yes" },
        { successMessage: "Participant added to Participant DB." },
      );
      return;
    }
    await saveVolunteerPatch(
      { IsVolunteerDatabase: 0, IsUseVolunteerDB: "No" },
      { successMessage: "Participant removed from Participant DB." },
    );
  };

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !canEdit) return;
    const id = getVolunteerRowId(volunteer) || volunteerId;
    if (!id) return;

    showLoader("Uploading document...");
    try {
      const dataUrl = await fileToDataUrl(file);
      const extension =
        String(file.name || "")
          .split(".")
          .pop()
          ?.toLowerCase() || "";
      await saveVolunteerDocument(userData, {
        volunteerIdRef: id,
        documentName: file.name,
        documentUrl: dataUrl,
        extension,
      });
      showAlert({ message: "Document uploaded.", type: "success" });
      await loadSideData();
      await refreshActivityLog();
    } catch (err) {
      showAlert({ message: err?.message || "Could not upload document." });
    } finally {
      hideLoader();
    }
  };

  if (accessLoading) {
    return <div className="participant-manager-page pm-page--bootstrapping" />;
  }
  if (!canViewParticipantProfile) {
    return <Navigate to="/notification-center" replace />;
  }

  const statusConfirmCopy =
    Number(statusConfirm.nextStatus) === 1
      ? {
          title: "Inactivate participant",
          message: `Set ${displayName} as Inactive? Clinical details will be hidden.`,
          confirmText: "Set Inactive",
          confirmButtonClass: "btn-warning",
          icon: "fas fa-pause-circle text-warning",
        }
      : {
          title: "Remove participant",
          message: `Remove ${displayName}? Clinical details will be deleted.`,
          confirmText: "Remove",
          confirmButtonClass: "btn-danger",
          icon: "fas fa-exclamation-triangle text-danger",
        };

  return (
    <div className="participant-manager-page pm-profile-shell">
      <PageBreadcrumb
        items={[
          { label: "Participant Manager", path: "/participantmanager/active" },
          { label: displayName },
        ]}
        actions={
          canAddParticipant
            ? [
                {
                  icon: "fas fa-user-plus",
                  label: "Add Participant",
                  onClick: () => setAddOpen(true),
                },
              ]
            : []
        }
      />

      <header className="pm-profile-hero">
        <div className="pm-profile-hero__identity">
          {canEdit ? (
            <div className="pm-profile-avatar-upload">
              <SimpleCircularUpload
                currentImage={
                  isUsablePhotoUrl(photoUrl) && !photoFailed ? photoUrl : null
                }
                defaultImage={null}
                size={60}
                maxFileSize={5}
                onChange={async (base64) => {
                  if (!base64) return;
                  try {
                    await saveVolunteerPatch(
                      { ProfilePictureUrl: base64 },
                      { successMessage: "Profile picture updated." },
                    );
                    setPhotoFailed(false);
                  } catch {
                    /* alert handled in saveVolunteerPatch */
                  }
                }}
              />
            </div>
          ) : showPhoto ? (
            <img
              className="pm-profile-avatar"
              src={photoUrl}
              alt=""
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <div
              className="pm-profile-avatar pm-profile-avatar--initials"
              aria-hidden="true"
            >
              {initials}
            </div>
          )}

          <div className="pm-profile-hero__text">
            <div className="pm-profile-hero__title-row">
              <h1 className="pm-profile-hero__name">{displayName}</h1>
              <HeroStatusControl
                volunteer={volunteer}
                onStatusChange={requestStatusChange}
                readOnly={profileReadOnly}
              />
              <button
                type="button"
                className={`pm-outline-badge ${
                  isOnParticipantDb
                    ? "pm-outline-badge--success"
                    : "pm-outline-badge--muted"
                }${canEdit ? " is-clickable" : ""}`}
                onClick={requestDbToggle}
                disabled={!canEdit || !volunteer}
                title={
                  canEdit
                    ? isOnParticipantDb
                      ? "Remove from Participant DB"
                      : "Add to Participant DB"
                    : "Participant DB status"
                }
              >
                {getParticipantDbLabel(volunteer?.IsUseVolunteerDB)}
                {canEdit ? (
                  <i className="fas fa-pencil-alt ms-1" aria-hidden="true" />
                ) : null}
              </button>
            </div>
            <p className="pm-profile-hero__meta">{metaBits.join(" · ")}</p>
            {profileReadOnly ? (
              <p className="pm-profile-hero__readonly-note">
                This participant is{" "}
                {getVolunteerStatusLabel(volunteer?.VolStatus)}. The profile is
                view-only — reactivate from the participant list to edit.
              </p>
            ) : null}
          </div>
        </div>

        <div className="pm-profile-hero__actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setShowCodes(true)}
            disabled={!volunteer}
          >
            Codes
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setBookOpen(true)}
            disabled={!volunteer || !canEdit}
            title={
              !canEdit
                ? "Booking is not available for inactive or removed participants"
                : undefined
            }
          >
            Book appointment
          </button>
        </div>
      </header>

      <div className="pm-profile-body">
        <div className="pm-profile-main">
          <nav className="pm-profile-tabs" aria-label="Participant sections">
            {visibleTabs.map((tab) => (
              <NavLink
                key={tab.slug || "details"}
                to={tab.slug ? `${basePath}/${tab.slug}` : basePath}
                end={!tab.slug}
                className={({ isActive }) =>
                  `pm-profile-tabs__link${isActive ? " is-active" : ""}`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <div className="pm-tab-panel">
            <Routes>
              <Route
                index
                element={
                  <ProfileDetailsPanel
                    volunteer={volunteer}
                    canEdit={canEdit}
                    ethnicityOptions={ethnicityOptions}
                    orgCountryIso2={orgCountryIso2}
                    onFieldSave={saveVolunteerPatch}
                    onSaveNhsNumber={saveNhsNumber}
                    onSaveNiNumber={saveNiNumber}
                  />
                }
              />
              {isOnParticipantDb ? (
                <Route
                  path="clinical"
                  element={
                    <ClinicalHistoryPanel
                      userData={userData}
                      volunteerId={volunteerId}
                      volunteer={volunteer}
                      canEdit={canEdit}
                      showAlert={showAlert}
                      showLoader={showLoader}
                      hideLoader={hideLoader}
                      initialSection={location.state?.clinicalSection}
                      onChanged={refreshActivityLog}
                    />
                  }
                />
              ) : (
                <Route
                  path="clinical"
                  element={<Navigate to={basePath} replace />}
                />
              )}
              <Route
                path="medication"
                element={
                  <Navigate
                    to={`${basePath}/clinical`}
                    replace
                    state={{ clinicalSection: "medication" }}
                  />
                }
              />
              <Route
                path="appointments"
                element={
                  <StudyHistoryPanel
                    userData={userData}
                    volunteerId={volunteerId}
                    volunteer={volunteer}
                    canEdit={canEdit}
                    onChanged={refreshActivityLog}
                  />
                }
              />
              <Route
                path="study-history"
                element={<Navigate to={`${basePath}/appointments`} replace />}
              />
              <Route
                path="payments"
                element={<Navigate to={`${basePath}/appointments`} replace />}
              />
              <Route
                path="documents"
                element={
                  <DocumentsPanel
                    documents={documents}
                    fileInputRef={fileInputRef}
                    onAddClick={() => fileInputRef.current?.click()}
                    onFileChange={handleDocumentUpload}
                    canEdit={canEdit}
                    userData={userData}
                  />
                }
              />
              <Route
                path="notes"
                element={
                  <NotesPanel
                    notes={notes}
                    userData={userData}
                    volunteerId={volunteerId}
                    canEdit={canEdit}
                    showAlert={showAlert}
                    showLoader={showLoader}
                    hideLoader={hideLoader}
                    onChanged={async () => {
                      const noteRows = await fetchVolunteerNotes(
                        userData,
                        volunteerId,
                      ).catch(() => []);
                      setNotes(noteRows);
                    }}
                  />
                }
              />
              <Route
                path="communications"
                element={<Navigate to={`${basePath}/notes`} replace />}
              />
              <Route
                path="audit"
                element={<AuditLogPanel rows={activityRows} />}
              />
              <Route
                path="activity"
                element={<Navigate to={`${basePath}/audit`} replace />}
              />
            </Routes>
          </div>
        </div>

        <aside className="pm-profile-aside">
          <AttendanceSidebarCard appointmentInfo={appointmentInfo} />
          <StudiesSidebarCard studyCounts={studyCounts} />
        </aside>
      </div>

      {showCodes && volunteer ? (
        <ParticipantCodeLabelModal
          row={volunteer}
          onClose={() => setShowCodes(false)}
        />
      ) : null}

      {canEdit ? (
        <AppointmentBookModal
          open={bookOpen}
          onClose={() => setBookOpen(false)}
          userData={userData}
          mode={BOOK_MODES.PREFILL_VOLUNTEER}
          initialVolunteer={volunteer}
          onSaved={() => {
            setBookOpen(false);
            loadSideData();
          }}
        />
      ) : null}

      <ConfirmModal
        isOpen={statusConfirm.open}
        onClose={() => setStatusConfirm({ open: false, nextStatus: null })}
        onConfirm={() => {
          const next = statusConfirm.nextStatus;
          setStatusConfirm({ open: false, nextStatus: null });
          if (next != null) applyStatusChange(next);
        }}
        title={statusConfirmCopy.title}
        message={statusConfirmCopy.message}
        confirmText={statusConfirmCopy.confirmText}
        cancelText="Cancel"
        confirmButtonClass={statusConfirmCopy.confirmButtonClass}
        icon={statusConfirmCopy.icon}
      />

      {IdVerifyModal}

      <ConfirmModal
        isOpen={dbConfirm.open}
        onClose={() => setDbConfirm({ open: false, nextOnList: null })}
        onConfirm={confirmDbChange}
        title={
          dbConfirm.nextOnList
            ? "Add to Participant DB"
            : "Remove from Participant DB"
        }
        message={
          dbConfirm.nextOnList
            ? `Has ${displayName} provided consent to join the Participant DB? Please check your local policy before continuing. Clinical history (including medication) will become available.`
            : `Remove ${displayName} from the Participant DB? They will show as Not in Participant DB, and the Clinical tab will be hidden.`
        }
        confirmText={
          dbConfirm.nextOnList ? "Add to Participant DB" : "Remove from DB"
        }
        cancelText="Cancel"
        confirmButtonClass={
          dbConfirm.nextOnList ? "btn-primary" : "btn-danger"
        }
        icon={
          dbConfirm.nextOnList
            ? "fas fa-user-plus text-primary"
            : "fas fa-user-slash text-danger"
        }
      />

      <AddParticipantModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </div>
  );
};

export default ParticipantProfile;
